import useInboxQueryParams from "hooks/useInboxQueryParams";
import { useEffect, useRef, useState } from "react";
import getQueryParamsCount from "./getQueryParamsCount";
import useOnClickOutside from "hooks/useOnClickOutside";
import getRadioTypeValues from "./getRadioTypeValues";
import getSelectedFromList from "./getSelectedFromList";

export type QueryParamDropdownListT = {
  [key: string]: (string | { name: string; type: "checkbox" | "radio" })[];
};

const useQueryParamDropdown = (
  list: QueryParamDropdownListT,
  onChange?: (value: QueryParamDropdownListT) => void
) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const { getQueryParam, setQueryParams } = useInboxQueryParams();
  const [selected, setSelected] = useState<QueryParamDropdownListT>(
    getSelectedFromList(list, getQueryParam)
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(dropdownRef, () => toggleShowDropdown(false));

  function _syncWithQueryParams() {
    const newSelected = getSelectedFromList(list, getQueryParam);
    setSelected((prevSelected) => ({ ...prevSelected, ...newSelected }));
  }
  useEffect(() => {
    _syncWithQueryParams();
  }, []);

  const toggleShowDropdown = (value?: boolean | any) => {
    setShowDropdown((prev) => value ?? !prev);
    _syncWithQueryParams();
  };

  const selectedCount = getQueryParamsCount(selected);

  const clear = () => {
    const reset = Object.keys(list).reduce(
      (acc: { [key: string]: string }, qp) => {
        acc[qp] = "";
        return acc;
      },
      {}
    );
    setQueryParams(reset);
    setSelected({});
    toggleShowDropdown();
    onChange?.({});
  };

  const apply = () => {
    const payload: { [key: string]: string } = {};

    for (const key in selected) {
      payload[key] = selected[key].join(",");
    }

    setQueryParams(payload);
    onChange?.(selected);
    toggleShowDropdown();
  };

  const select = (section: string, qp: string, type: "checkbox" | "radio") => {
    if (type === "radio") {
      // select only passed param amd remove others.
      // imitating radio button
      setSelected((prev) => ({
        ...prev,
        [section]: prev?.[section]?.[0] !== qp ? [qp] : [],
      }));
    } else {
      const isAlreadySelected = selected[section]?.includes(qp);

      const res = isAlreadySelected
        ? selected[section]?.slice()?.filter((el) => el !== qp)
        : [...(selected[section] || []), qp];

      setSelected((prev) => ({
        ...prev,
        [section]: res.filter(
          (param) =>
            // uncheck all radio buttons
            !getRadioTypeValues(list[section]).includes(param as string)
        ),
      }));
    }
  };

  return {
    clear,
    apply,
    select,
    toggleShowDropdown,
    selectedCount,
    showDropdown,
    selected,
    dropdownRef,
  };
};

export default useQueryParamDropdown;
